import { Input, Label } from "@roberttamas/ui";
import { useTranslation } from "react-i18next";

import { ColorSchemeEditor } from "@/components/color-scheme-editor";
import { FontSchemeEditor } from "@/components/font-scheme-editor";
import type { TemplateDraft } from "@/lib/templates";

interface TemplateEditorProps {
  value: TemplateDraft;
  onChange: (next: TemplateDraft) => void;
}

/**
 * Template authoring form: a name plus the shared color and font editors (the
 * same AI / seed / detailed tools used on a site). Controlled — the parent
 * owns the draft and persistence.
 */
export function TemplateEditor({ value, onChange }: TemplateEditorProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<TemplateDraft>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Label htmlFor="tmpl-name">{t("themeEditor.nameLabel")}</Label>
        <Input
          id="tmpl-name"
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder={t("themeEditor.namePlaceholder")}
        />
      </div>

      <section className="space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{t("themeEditor.colorsTitle")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("themeEditor.colorsHint")}
          </p>
        </div>
        <ColorSchemeEditor
          value={value.colorScheme}
          onChange={(colorScheme) => set({ colorScheme })}
        />
      </section>

      <section className="space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{t("themeEditor.fontsTitle")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("themeEditor.fontsHint")}
          </p>
        </div>
        <FontSchemeEditor
          value={value.fontScheme}
          onChange={(fontScheme) => set({ fontScheme })}
        />
      </section>
    </div>
  );
}
